// Original utils.ts port
import moment, { Moment } from 'moment';
import _ from 'lodash';

import { Chart } from 'chart.js';

export function updateChartTheme(chart: Chart | undefined) {
    if (!chart || !chart.options) return;
    const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
    const textColor = isDark ? '#dee2e6' : '#666';
    const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';

    if (chart.options.title) chart.options.title.fontColor = textColor;
    if (chart.options.legend && chart.options.legend.labels) chart.options.legend.labels.fontColor = textColor;
    
    const updateScales = (axes: any[]) => {
        if (!axes) return;
        axes.forEach(axis => {
            if (axis.ticks) axis.ticks.fontColor = textColor;
            if (axis.gridLines) axis.gridLines.color = gridColor;
        });
    };

    updateScales(chart.options.scales?.yAxes as any[]);
    updateScales(chart.options.scales?.xAxes as any[]);
    
    chart.update();
}

export function getCommonChartOptions(title: string) {
    const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
    const textColor = isDark ? '#dee2e6' : '#666';
    const gridColor = isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
    
    return {
        title: { display: true, text: title, fontColor: textColor },
        legend: { labels: { fontColor: textColor } },
        scales: {
            yAxes: [{ 
                ticks: { beginAtZero: true, fontColor: textColor },
                gridLines: { color: gridColor }
            }],
            xAxes: [{
                ticks: { fontColor: textColor },
                gridLines: { color: gridColor }
            }]
        }
    };
}

export function getDoughnutChartOptions(title: string) {
    const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
    const textColor = isDark ? '#dee2e6' : '#666';
    
    return {
        title: { display: true, text: title, fontColor: textColor },
        legend: { labels: { fontColor: textColor } },
        animation: { animateScale: true, animateRotate: true }
    };
}

export function watchThemeChanges(callback: () => void) {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-bs-theme') {
                callback();
            }
        });
    });
    observer.observe(document.documentElement, { attributes: true });
}

export const CANADIAN_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Northwest Territories' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' }
];

// Single implementation (remove earlier duplicates)
export function getUoMFilteredList(args: { uomList: any[]; servicesList?: { id: number; unitOfMeasurement: string }[]; serviceId?: number; serviceUomAbbr?: string } = { uomList: [] }) {
  let { uomList, servicesList, serviceId, serviceUomAbbr } = args;
  if (serviceId) {
    const selectedService = servicesList?.find(srv => serviceId == srv.id);
    serviceUomAbbr = selectedService?.unitOfMeasurement;
  }
  const selectedServiceUom = uomList.find(uom => serviceUomAbbr == uom.abbr);
  return uomList.filter(uom => selectedServiceUom?.measure == uom.measure);
}

export function getDateRange(startDate: string | Date | Moment, endDate: Date | Moment): Moment[] {
  const m_startDate = moment.utc(startDate).hours(0).minutes(0).seconds(0).milliseconds(0);
  const m_endDate = moment.utc(endDate).hours(0).minutes(0).seconds(0).milliseconds(0);
  const now = m_startDate, dates: Moment[] = [];
  while (now.isSameOrBefore(m_endDate)) { dates.push(moment(now)); now.add(1, 'days'); }
  return dates;
}

export function getDatetimePickerUTCDate(datetimepickerDiv: string, datetimepickerInput: string, max: boolean = false): Moment | null {
  const element = document.querySelector(datetimepickerDiv) as HTMLElement | null;
  if (!element || !(window as any).tempusDominus) return null;
  const picker = (window as any).tempusDominus?.find?.(element) as any;
  const jsDate: Date | null = picker?.dates?.lastPicked?.toDate?.() ?? picker?.viewDate?.toDate?.() ?? null;
  if (!jsDate) return null;
  const date = moment.utc(jsDate);
  if (max) date.endOf('day'); else date.startOf('day');
  return date;
}

export function aCC_toString(costCentersListWithACC: { name: string; AssociatedCostCenter?: { percentage: number } }[]) {
  const cell: string[] = [];
  for (const cc of costCentersListWithACC) {
    const pct = cc.AssociatedCostCenter?.percentage ?? 0;
    cell.push(`<span class="fw-bold ">${cc.name}</span>: ${pct}%`);
  }
  return cell.join('&nbsp&nbsp|&nbsp&nbsp</span>');
}

export type MessageDisplayData = { messages: string[]; service: { name: string } };
type DisplayMessagesData = { messages: string[]; service?: { name: string } };
export function displayMessages(mainContainerSelector: string, msgContainerSelector: string, data: DisplayMessagesData | DisplayMessagesData[]) {
  let html = '<ul>';
  if (data instanceof Array) {
    for (const entry of data) {
      for (const msg of _.uniq(entry.messages)) html += entry.service ? `<li><strong>${entry.service.name}</strong>: ${msg}</li>` : `<li>${msg}</li>`;
    }
  } else {
    for (const msg of _.uniq(data.messages)) html += data.service ? `<li><strong>${data.service.name}</strong>: ${msg}</li>` : `<li>${msg}</li>`;
  }
  html += '</ul>';
  
  const msgContainer = document.querySelector(msgContainerSelector);
  const mainContainer = document.querySelector(mainContainerSelector) as HTMLElement;
  
  if (html.length > 9) { 
      if (msgContainer) msgContainer.innerHTML = html;
      if (mainContainer) mainContainer.hidden = false;
  } else { 
      if (mainContainer) mainContainer.hidden = true;
  }
}

export function formatNumber(num: number | string, decimals: number = 2): string {
  if (typeof num !== 'number' && typeof num !== 'string') throw Error('Num parameter must be a Number or a String');
  num = parseFloat(num as string);
  if (isNaN(num)) throw Error('Num is not a valid number');
  const n = (num as number).toFixed(decimals).split('.');
  n[0] = n[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  return n.join('.');
}

export type Theme = { colors: { primary: string; secondary: string; success: string; info: string; warning: string; danger: string; light: string; dark: string } };
export function getTheme(): Theme {
  const style = getComputedStyle(document.body);
  const THEME = { colors: {} } as Theme;
  THEME.colors.primary = style.getPropertyValue('--primary');
  THEME.colors.secondary = style.getPropertyValue('--secondary');
  THEME.colors.success = style.getPropertyValue('--success');
  THEME.colors.info = style.getPropertyValue('--info');
  THEME.colors.warning = style.getPropertyValue('--warning');
  THEME.colors.danger = style.getPropertyValue('--danger');
  THEME.colors.light = style.getPropertyValue('--light');
  THEME.colors.dark = style.getPropertyValue('--dark');
  return THEME;
}

// https://getbootstrap.com/docs/5.3/customize/color/#all-colors
export const CHARTS_COLORS = {
  red: '#f1aeb5',      // red-200
  orange: '#ffcf99',   // orange-200
  yellow: '#ffe69c',   // yellow-200
  green: '#a3cfbb',    // green-200
  teal: '#a6e9d5',     // teal-200
  cyan: '#9eeaf9',     // cyan-200
  blue: '#9ec5fe',     // blue-200
  indigo: '#c29ffa',   // indigo-200
  purple: '#c5b3e6',   // purple-200
  pink: '#f1a8c7',     // pink-200
  grey: '#c4c8cb'      // gray-300 approximating a -200-like light grey
};

export function initPage() {
  document.querySelectorAll('li.active').forEach(el => el.classList.remove('active'));
  const viewNameInput = document.getElementById('globals-view-name') as HTMLInputElement;
  const viewName = viewNameInput?.value;
  if (viewName) {
      const navItem = document.getElementById('nav-' + viewName);
      if (navItem) navItem.classList.add('active');
  }
}

export function capitalize(str: string) {
  if (str.length === 0) {
    return ""; // Handle empty strings
  }
  return str.charAt(0).toUpperCase() + str.slice(1);
}

let pendingDtState: string | null = null;
const updateUrlWithDtState = _.debounce(() => {
  if (!pendingDtState) return;
  const currentUrl = new URL(window.location.href);
  const currentState = currentUrl.searchParams.get('dtState');
  
  if (currentState === pendingDtState) {
    pendingDtState = null;
    return;
  }

  currentUrl.searchParams.set('dtState', pendingDtState);
  window.history.replaceState(null, '', currentUrl.toString());
  pendingDtState = null;
}, 750);

export function dtStateSaveCallback(settings: any, data: any) {
  const state = JSON.stringify(data);
  pendingDtState = btoa(encodeURIComponent(state));
  updateUrlWithDtState();
}

export function dtStateLoadCallback(settings: any) {
  const url = new URL(window.location.href);
  const val = url.searchParams.get('dtState');
  if (!val) return null;
  try {
    return JSON.parse(decodeURIComponent(atob(val)));
  } catch (e) { return null; }
}

// (Removed duplicate minimal ports)